import React, {useEffect, useRef, useCallback, useState, useMemo} from 'react';
import lottie, {AnimationItem} from 'lottie-web';
import {motion} from 'motion/react';
import Svga from '@/controller/svg.controller';
import json from '@assets/gold-coin/data.json';
import img1 from '@assets/gold-coin/images/img_1.png';
import img2 from '@assets/gold-coin/images/img_2.png';
import img3 from '@assets/gold-coin/images/img_3.png';
import {CongratsAnimationModalProps, TextModification} from './types';

const CongratsAnimationModal: React.FC<CongratsAnimationModalProps> = ({
  congratsImage,
  congratsImageWidth = 927,
  congratsImageHeight = 151,
  textConfig,
  bottomContent,
}) => {
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showBottomContent, setShowBottomContent] = useState(false);

  // 修改Lottie文本样式的函数
  const modifyTextStyles = useCallback(
    (
      animationData: any,
      textModifications?: TextModification,
      replaceId = '$1',
    ) => {
      if (!textModifications) {
        return animationData;
      }

      if (animationData.assets && Array.isArray(animationData.assets)) {
        animationData.assets.forEach((asset: any) => {
          if (asset.id === 'comp_0' && asset.layers) {
            asset.layers.forEach((layer: any) => {
              // 找到文本图层 (ty: 5) 并匹配 ID
              if (layer.ty === 5 && layer.id === replaceId) {
                const textData = layer.t?.d?.k?.[0]?.s;
                if (textData && textModifications.textContent) {
                  textData.t = textModifications.textContent;
                }
                // 可选：修改字体大小
                if (textData && textModifications.fontSize) {
                  textData.s = textModifications.fontSize;
                }
                // 可选：修改字体颜色
                if (textData && textModifications.fontColor) {
                  textData.fc = textModifications.fontColor;
                }
                // 可选：修改描边颜色
                if (textData && textModifications.strokeColor) {
                  textData.sc = textModifications.strokeColor;
                }
                // 可选：修改描边宽度
                if (textData && textModifications.strokeWidth) {
                  textData.sw = textModifications.strokeWidth;
                }
                // 可选：修改对齐方式
                if (
                  textData &&
                  typeof textModifications.justification === 'number'
                ) {
                  textData.j = textModifications.justification;
                }

                // 可选：修改位置偏移
                if (
                  layer.ks &&
                  (textModifications.offsetX || textModifications.offsetY)
                ) {
                  if (!layer.ks.p) {
                    layer.ks.p = {a: 0, k: [0, 0]};
                  }
                  if (layer.ks.p.k && Array.isArray(layer.ks.p.k)) {
                    if (textModifications.offsetX) {
                      layer.ks.p.k[0] =
                        (layer.ks.p.k[0] || 0) + textModifications.offsetX;
                    }
                    if (textModifications.offsetY) {
                      layer.ks.p.k[1] =
                        (layer.ks.p.k[1] || 0) + textModifications.offsetY;
                    }
                  }
                }
              }
            });
          }
        });
      }
      return animationData;
    },
    [],
  );

  // 缓存处理后的动画数据
  const processedAnimationData = useMemo(() => {
    // 图片映射
    const imageMap = {
      'img_0.png': congratsImage,
      'img_1.png': img1,
      'img_2.png': img2,
      'img_3.png': img3,
    };

    // 创建动画数据的副本
    let modifiedData = JSON.parse(JSON.stringify(json));

    // 替换图片资源
    if (modifiedData.assets && Array.isArray(modifiedData.assets)) {
      modifiedData.assets.forEach((asset: any) => {
        if (asset.p && imageMap[asset.p as keyof typeof imageMap]) {
          // 删除相对路径，直接使用导入的图片URL
          delete asset.u;
          const originalP = asset.p;
          asset.p = imageMap[asset.p as keyof typeof imageMap];

          // 设置 img_0.png 的宽高
          if (originalP === 'img_0.png') {
            asset.w = congratsImageWidth;
            asset.h = congratsImageHeight;
          }
        }
      });
    }

    // 应用文本配置
    if (textConfig) {
      Object.entries(textConfig).forEach(([textId, textModification]) => {
        modifiedData = modifyTextStyles(modifiedData, textModification, textId);
      });
    }

    return modifiedData;
  }, [
    congratsImage,
    congratsImageWidth,
    congratsImageHeight,
    textConfig,
    modifyTextStyles,
  ]);

  const svgaCanvas = useRef<HTMLCanvasElement>(null);
  // svga 动画控制器
  const svgaPlayer = useRef<Svga | null>(null);
  const svgaInit = useCallback(() => {
    if (svgaCanvas.current && !svgaPlayer.current) {
      const root = document.getElementById('root');
      const rect = root!.getBoundingClientRect();
      svgaCanvas.current.setAttribute('width', `${rect.width}`);
      svgaCanvas.current.setAttribute('height', `${rect.height}`);
      svgaPlayer.current = new Svga({
        node: svgaCanvas.current,
        dir: '/assets/color-play.svga',
        finish: () => {
          svgaPlayer.current?.stop();
          svgaPlayer.current?.play();
        },
      });
    } else {
      svgaPlayer.current?.play();
    }
  }, []);

  // Lottie 动画
  useEffect(() => {
    if (!lottieContainerRef.current) {
      setIsLoading(false);
      setShowBottomContent(false);
      return;
    }

    const loadAnimation = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        if (!lottieContainerRef.current) {
          return;
        }

        animationRef.current = lottie.loadAnimation({
          container: lottieContainerRef.current,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          animationData: processedAnimationData,
        });
        animationRef.current.setSpeed(0.5);

        // 监听动画加载完成事件
        animationRef.current.addEventListener('DOMLoaded', () => {
          setIsLoading(false);
          setShowBottomContent(true);
          svgaInit();
        });
        // 监听动画错误事件
        animationRef.current.addEventListener('error', () => {
          setHasError(true);
          setIsLoading(false);
        });
        animationRef.current.addEventListener('complete', () => {
          animationRef.current?.goToAndPlay(20, true);
        });
      } catch (error) {
        console.error('加载动画失败:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    loadAnimation();

    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [processedAnimationData, svgaInit]);

  return (
    <div className="relative">
      <canvas
        ref={svgaCanvas}
        className="fixed z-10 left-0 top-0 w-full h-full pointer-events-none"
      />

      {/* 加载状态 */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-10">
          <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* 错误状态 */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
          <div className="text-white text-center">
            <div className="text-lg mb-2">Loading Failed</div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-yellow-400 text-black rounded">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Lottie 动画容器 */}
      <div ref={lottieContainerRef} className="w-full h-full" />

      {/* 底部内容区域 */}
      {showBottomContent && bottomContent && (
        <motion.div
          className="absolute bottom-0 left-0 right-0"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={{delay: 0.3, duration: 0.5}}>
          {bottomContent}
        </motion.div>
      )}
    </div>
  );
};

export default CongratsAnimationModal;
